/**
 * ImportErrorDisplay - Componente para exibição de erros estruturados de importação
 *
 * Epic 51 - Erros descritivos para importação de Excel
 *
 * Exibe cards ricos com:
 * - Ícones específicos por tipo de erro
 * - Detalhes formatados (CNPJ, produtos, linhas)
 * - Sugestões de correção
 * - Cores por severidade (vermelho = bloqueante, laranja = parcial)
 */
import React from 'react';
import { AlertTriangle, FileX2, Package, AlertCircle, XCircle, Lightbulb } from 'lucide-react';

// Tipos de erro suportados
type ErrorType = 'CNPJ_MISMATCH' | 'INVALID_STRUCTURE' | 'PRODUCTS_NOT_FOUND' | 'DATA_VALIDATION' | 'INVALID_CLIENT' | 'GENERIC';

// Interface para produto não encontrado
interface ProdutoNaoEncontrado {
  codigo: string;
  descricao?: string | null;
  linha?: number;
}

// Interface para erro de linha
interface ErroLinha {
  linha: number;
  campo?: string;
  valor?: string;
  erro: string;
}

// Interface para detalhes do erro estruturado
interface ErrorDetails {
  // CNPJ_MISMATCH
  cnpj_arquivo?: string;
  nome_fornecedor_arquivo?: string | null;
  cnpj_selecionado?: string;
  cliente_selecionado?: string;

  // INVALID_STRUCTURE
  colunas_encontradas?: number;
  colunas_minimas?: number;
  colunas_obrigatorias?: string[];

  // PRODUCTS_NOT_FOUND
  cliente?: string;
  total_produtos_erro?: number;
  produtos?: ProdutoNaoEncontrado[];

  // DATA_VALIDATION
  erros_linha?: ErroLinha[];
  total_linhas?: number;
  linhas_processadas?: number;

  // INVALID_CLIENT
  cliente_id?: number;
}

// Interface para erro estruturado da API
interface StructuredError {
  error_type: ErrorType;
  error: string;
  details?: ErrorDetails;
  sugestao?: string;
}

// Props do componente
interface ImportErrorDisplayProps {
  error: StructuredError | string | null;
}

/**
 * Parseia o erro para formato estruturado
 */
function parseError(error: StructuredError | string | null): StructuredError | null {
  if (!error) return null;

  // Se já é objeto estruturado
  if (typeof error === 'object' && 'error_type' in error) {
    return error;
  }

  // Se é string, criar erro genérico
  if (typeof error === 'string') {
    return {
      error_type: 'GENERIC',
      error: error,
    };
  }

  // Se é objeto mas sem error_type (formato antigo)
  if (typeof error === 'object') {
    const errObj = error as Record<string, unknown>;
    if (errObj.error && typeof errObj.error === 'string') {
      return {
        error_type: 'GENERIC',
        error: errObj.error as string,
        details: errObj.details as ErrorDetails,
      };
    }
  }

  return {
    error_type: 'GENERIC',
    error: 'Erro desconhecido ao processar o arquivo',
  };
}

/**
 * Card de erro para CNPJ incompatível
 */
function CnpjMismatchCard({ error }: { error: StructuredError }) {
  const details = error.details;

  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h4 className="text-base font-semibold text-red-800 dark:text-red-300">
            Cliente do arquivo não corresponde ao selecionado
          </h4>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            O arquivo Excel pertence a outro cliente
          </p>
        </div>
      </div>

      {/* Detalhes */}
      <div className="space-y-3 pl-11">
        {/* Arquivo Excel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-red-100 dark:border-red-900/50">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <FileX2 className="w-4 h-4" />
            <span>Arquivo Excel:</span>
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            CNPJ: {details?.cnpj_arquivo || 'N/A'}
            {details?.nome_fornecedor_arquivo && (
              <span className="text-gray-600 dark:text-gray-400 ml-2">
                ({details.nome_fornecedor_arquivo})
              </span>
            )}
          </div>
        </div>

        {/* Cliente Selecionado */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-green-900/50">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Package className="w-4 h-4 text-green-600" />
            <span>Cliente Selecionado:</span>
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            CNPJ: {details?.cnpj_selecionado || 'N/A'}
            {details?.cliente_selecionado && (
              <span className="text-gray-600 dark:text-gray-400 ml-2">
                ({details.cliente_selecionado})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Sugestão */}
      {error.sugestao && (
        <div className="flex items-start gap-2 mt-4 pl-11 text-sm text-amber-700 dark:text-amber-400">
          <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error.sugestao}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Card de erro para estrutura inválida
 */
function InvalidStructureCard({ error }: { error: StructuredError }) {
  const details = error.details;
  const colunasObrigatorias = details?.colunas_obrigatorias || [
    'Código',
    'Descrição',
    'Lote',
    'Validade',
    'Quantidade',
  ];

  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
          <FileX2 className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h4 className="text-base font-semibold text-red-800 dark:text-red-300">
            Estrutura do arquivo inválida
          </h4>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            Encontradas {details?.colunas_encontradas || 0} colunas, mínimo necessário:{' '}
            {details?.colunas_minimas || 5}
          </p>
        </div>
      </div>

      {/* Lista de colunas obrigatórias */}
      <div className="pl-11">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Colunas obrigatórias:</p>
        <div className="grid grid-cols-2 gap-2">
          {colunasObrigatorias.map((col, idx) => (
            <div
              key={col}
              className="flex items-center gap-2 text-sm bg-white dark:bg-gray-800 px-3 py-1.5 rounded border border-gray-200 dark:border-gray-700"
            >
              <span className="font-mono text-gray-500 dark:text-gray-400">
                {String.fromCharCode(65 + idx)}
              </span>
              <span className="text-gray-900 dark:text-white">{col}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sugestão */}
      {error.sugestao && (
        <div className="flex items-start gap-2 mt-4 pl-11 text-sm text-amber-700 dark:text-amber-400">
          <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error.sugestao}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Card de erro para produtos não encontrados
 */
function ProductsNotFoundCard({ error }: { error: StructuredError }) {
  const details = error.details;
  const produtos = details?.produtos || [];
  const totalErros = details?.total_produtos_erro || produtos.length;

  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
          <Package className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h4 className="text-base font-semibold text-red-800 dark:text-red-300">
            {totalErros} produto(s) não cadastrado(s)
          </h4>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">
            Para o cliente: {details?.cliente || 'N/A'}
          </p>
        </div>
      </div>

      {/* Lista de produtos */}
      <div className="pl-11">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="max-h-48 overflow-y-auto">
            {produtos.map((produto, idx) => (
              <div
                key={`${produto.codigo}-${idx}`}
                className={`flex items-center gap-3 px-3 py-2 text-sm ${
                  idx !== produtos.length - 1
                    ? 'border-b border-gray-100 dark:border-gray-700'
                    : ''
                }`}
              >
                <span className="font-mono text-red-600 dark:text-red-400 font-medium">
                  {produto.codigo}
                </span>
                {produto.descricao && (
                  <span className="text-gray-600 dark:text-gray-400 truncate flex-1">
                    {produto.descricao}
                  </span>
                )}
                {produto.linha && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    linha {produto.linha}
                  </span>
                )}
              </div>
            ))}
          </div>
          {totalErros > produtos.length && (
            <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
              ... e mais {totalErros - produtos.length} produto(s)
            </div>
          )}
        </div>
      </div>

      {/* Sugestão */}
      {error.sugestao && (
        <div className="flex items-start gap-2 mt-4 pl-11 text-sm text-amber-700 dark:text-amber-400">
          <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error.sugestao}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Card de erro para validação de dados (laranja - permite parcial)
 */
function DataValidationCard({ error }: { error: StructuredError }) {
  const details = error.details;
  const erros = details?.erros_linha || [];

  return (
    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
          <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h4 className="text-base font-semibold text-orange-800 dark:text-orange-300">
            {erros.length} erro(s) encontrado(s) durante o processamento
          </h4>
          {details?.linhas_processadas !== undefined && details?.total_linhas !== undefined && (
            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
              {details.linhas_processadas} de {details.total_linhas} linhas processadas com
              sucesso
            </p>
          )}
        </div>
      </div>

      {/* Lista de erros */}
      {erros.length > 0 && (
        <div className="pl-11">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="max-h-40 overflow-y-auto">
              {erros.map((err, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-2 px-3 py-2 text-sm ${
                    idx !== erros.length - 1
                      ? 'border-b border-gray-100 dark:border-gray-700'
                      : ''
                  }`}
                >
                  <span className="font-mono text-orange-600 dark:text-orange-400 font-medium whitespace-nowrap">
                    Linha {err.linha}:
                  </span>
                  <span className="text-gray-700 dark:text-gray-300">{err.erro}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sugestão */}
      {error.sugestao && (
        <div className="flex items-start gap-2 mt-4 pl-11 text-sm text-amber-700 dark:text-amber-400">
          <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error.sugestao}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Card de erro genérico (fallback)
 */
function GenericErrorCard({ error }: { error: StructuredError }) {
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
          <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1">
          <h4 className="text-base font-semibold text-red-800 dark:text-red-300">
            Erro na Importação
          </h4>
          <p className="text-sm text-red-700 dark:text-red-300 mt-2">{error.error}</p>

          {/* Sugestão */}
          {error.sugestao && (
            <div className="flex items-start gap-2 mt-3 text-sm text-amber-700 dark:text-amber-400">
              <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error.sugestao}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Componente principal que seleciona o card apropriado
 */
export const ImportErrorDisplay: React.FC<ImportErrorDisplayProps> = ({ error }) => {
  const parsedError = parseError(error);

  if (!parsedError) return null;

  switch (parsedError.error_type) {
    case 'CNPJ_MISMATCH':
      return <CnpjMismatchCard error={parsedError} />;
    case 'INVALID_STRUCTURE':
      return <InvalidStructureCard error={parsedError} />;
    case 'PRODUCTS_NOT_FOUND':
      return <ProductsNotFoundCard error={parsedError} />;
    case 'DATA_VALIDATION':
      return <DataValidationCard error={parsedError} />;
    case 'INVALID_CLIENT':
    case 'GENERIC':
    default:
      return <GenericErrorCard error={parsedError} />;
  }
};

export default ImportErrorDisplay;
